/**
 * useFleet — single source of truth for the live fleet view of a company.
 *
 * Hydrates from REST (initial data + cache key) and then keeps the cache
 * fresh via WS `fleet.snapshot` and `fleet.delta` envelopes.
 *
 * The reducer is exported as `applyFleetDelta` so it can be unit-tested
 * without spinning up a WebSocket.
 */

import { useEffect } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type {
  Envelope,
  FleetDelta,
  FleetSnapshot,
  WorkOrderUpdate,
} from "@paperclip-cloud/shared";
import { ws } from "../api/ws";
import { devicesApi, type Device } from "../api/queries/devices";
import { workOrdersApi, type WorkOrderRow } from "../api/queries/work_orders";

export type FleetCache = {
  devices: Device[];
  active_orders: WorkOrderRow[];
};

export const fleetKey = (companyId: string) => ["fleet", companyId] as const;

export function applyFleetSnapshot(
  prev: FleetCache | undefined,
  snap: FleetSnapshot,
): FleetCache {
  const devices: Device[] = snap.devices.map((d) => ({
    id: d.id,
    company_id: snap.company_id,
    name: d.name,
    hostname: d.hostname,
    online: d.online,
    pairing_state: d.pairing_state,
    capabilities: d.capabilities,
    current_step_id: d.current_step_id,
    last_seen_at: d.last_seen_at,
    created_at: prev?.devices.find((x) => x.id === d.id)?.created_at ?? d.last_seen_at,
  }));
  const active_orders: WorkOrderRow[] = snap.active_work_orders.map((o) => ({
    id: o.id,
    company_id: snap.company_id,
    requested_by_user_id: "",
    title: o.title,
    description: "",
    status: o.status,
    ceo_session_id: null,
    created_at: o.created_at,
    updated_at: o.created_at,
  }));
  return { devices, active_orders };
}

export function applyFleetDelta(
  prev: FleetCache | undefined,
  delta: FleetDelta,
): FleetCache {
  const base: FleetCache = prev ?? { devices: [], active_orders: [] };
  let devices = base.devices.slice();
  let orders = base.active_orders.slice();

  for (const c of delta.changes) {
    switch (c.kind) {
      case "device.online": {
        devices = devices.map((d) =>
          d.id === c.device_id ? { ...d, online: true, last_seen_at: Date.now() } : d,
        );
        break;
      }
      case "device.offline": {
        devices = devices.map((d) =>
          d.id === c.device_id ? { ...d, online: false } : d,
        );
        break;
      }
      case "device.added": {
        const d = c.device;
        if (devices.some((x) => x.id === d.id)) break;
        devices = [
          ...devices,
          {
            id: d.id,
            company_id: delta.company_id,
            name: d.name,
            hostname: d.hostname,
            online: d.online,
            pairing_state: d.pairing_state,
            capabilities: d.capabilities,
            current_step_id: d.current_step_id,
            last_seen_at: d.last_seen_at,
            created_at: d.last_seen_at,
          },
        ];
        break;
      }
      case "device.removed": {
        devices = devices.filter((d) => d.id !== c.device_id);
        break;
      }
      case "device.updated": {
        devices = devices.map((d) =>
          d.id === c.device_id ? { ...d, ...(c.patch as Partial<Device>) } : d,
        );
        break;
      }
      case "work_order.added": {
        const o = c.work_order;
        if (orders.some((x) => x.id === o.id)) break;
        orders = [
          {
            id: o.id,
            company_id: delta.company_id,
            requested_by_user_id: "",
            title: o.title,
            description: "",
            status: o.status,
            ceo_session_id: null,
            created_at: o.created_at,
            updated_at: o.created_at,
          },
          ...orders,
        ];
        break;
      }
      case "work_order.updated": {
        orders = orders.map((o) =>
          o.id === c.work_order_id ? { ...o, ...(c.patch as Partial<WorkOrderRow>) } : o,
        );
        // Drop terminal orders from the "active" list
        orders = orders.filter(
          (o) => !["complete", "failed", "cancelled"].includes(o.status),
        );
        break;
      }
    }
  }

  return { devices, active_orders: orders };
}

export function applyWorkOrderUpdate(
  prev: FleetCache | undefined,
  upd: WorkOrderUpdate,
): FleetCache {
  if (!prev) return { devices: [], active_orders: [] };
  const orders = prev.active_orders.map((o) =>
    o.id === upd.work_order_id ? { ...o, status: upd.status, updated_at: Date.now() } : o,
  );
  return {
    devices: prev.devices,
    active_orders: orders.filter(
      (o) => !["complete", "failed", "cancelled"].includes(o.status),
    ),
  };
}

/** Wire WS fan-in into a TanStack Query cache. Used by the page hook below. */
export function bindFleetToQueryClient(qc: QueryClient, companyId: string): () => void {
  ws.send("fleet.subscribe", { company_id: companyId }, { company_id: companyId });

  const off = ws.subscribe((env: Envelope) => {
    if (env.t === "fleet.snapshot") {
      const snap = env.body as FleetSnapshot;
      if (snap.company_id !== companyId) return;
      qc.setQueryData<FleetCache>(fleetKey(companyId), (prev) =>
        applyFleetSnapshot(prev, snap),
      );
    } else if (env.t === "fleet.delta") {
      const delta = env.body as FleetDelta;
      if (delta.company_id !== companyId) return;
      qc.setQueryData<FleetCache>(fleetKey(companyId), (prev) =>
        applyFleetDelta(prev, delta),
      );
    } else if (env.t === "work_order.update") {
      const upd = env.body as WorkOrderUpdate;
      qc.setQueryData<FleetCache>(fleetKey(companyId), (prev) =>
        applyWorkOrderUpdate(prev, upd),
      );
    }
  });
  return off;
}

export function useFleet(companyId: string | null) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: companyId ? fleetKey(companyId) : ["fleet", "none"],
    enabled: !!companyId,
    queryFn: async (): Promise<FleetCache> => {
      // `allSettled` so a transient failure on one endpoint doesn't blank
      // the dashboard. Devices are the critical surface — without them the
      // user can't pair, transfer, or chat. Work orders degrade gracefully
      // to an empty list with a console warning.
      const [devicesRes, ordersRes] = await Promise.allSettled([
        devicesApi.list(companyId!),
        workOrdersApi.list(companyId!, { limit: 10 }),
      ]);
      if (devicesRes.status === "rejected") throw devicesRes.reason;
      if (ordersRes.status === "rejected") {
        console.warn("[useFleet] work-orders fetch failed, rendering empty list", ordersRes.reason);
      }
      return {
        devices: devicesRes.value.devices,
        active_orders: ordersRes.status === "fulfilled" ? ordersRes.value.orders : [],
      };
    },
    staleTime: 5_000,
  });

  useEffect(() => {
    if (!companyId) return;
    const off = bindFleetToQueryClient(qc, companyId);
    return off;
  }, [qc, companyId]);

  return query;
}
